import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { formatUSDT, formatDate, shortenAddress } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';

/**
 * Admin panel showing:
 *  - Contract-wide totals (users, business, withdrawals, mining/level income paid)
 *    via getGlobalStats() — single call, instant.
 *  - User lookup by ID or wallet address.
 */
const PAGE_SIZE = 10;

export default function PlatformStatsPanel() {
  const { read } = useContracts();
  const notify = useNotify();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lookupErr, setLookupErr] = useState('');

  // Paginated users list — DEFERRED. Nothing is fetched until admin clicks "Load Users".
  const [page, setPage] = useState(1);
  const [pageRows, setPageRows] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false); // true once admin opts in

  async function loadStats() {
    setStatsLoading(true);
    try {
      const s = await read.storage.getGlobalStats();
      setStats({
        totalUsers:        Number(s.totalUsersCount),
        totalInvestment:   s.totalInvestmentUSDT,
        totalWithdrawUsdt: s.totalWithdrawnInUsdtUSDT,
        totalWithdrawXt:   s.totalWithdrawnInXtUSDT,
        totalMining:       s.totalMiningPaidUSDT,
        totalLevel:        s.totalLevelPaidUSDT,
      });
    } catch (err) {
      notify.error(parseError(err) || 'Could not load global stats.');
    } finally {
      setStatsLoading(false);
    }
  }

  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, []);

  // Fetch one page of the users list
  async function loadPage(p) {
    if (!stats || stats.totalUsers === 0) { setPageRows([]); return; }
    setPageLoading(true);
    try {
      const start = (p - 1) * PAGE_SIZE;
      const ids = await read.storage.getAllUserIds(start, PAGE_SIZE);
      const rows = await Promise.all(
        ids.map(async (id) => {
          try {
            const [b, pkgCount] = await Promise.all([
              read.storage.getUserBasic(id),
              read.storage.getPackageCount(id).catch(() => ethers.BigNumber.from(0)),
            ]);
            return {
              id: Number(id),
              wallet: b.wallet,
              sponsorId: Number(b.sponsorId),
              totalInvestment: b.totalInvestment,
              pendingBalance: b.pendingBalance,
              directs: Number(b.directs),
              isActive: b.isActive,
              isBlacklisted: b.isBlacklisted,
              pkgCount: Number(pkgCount),
            };
          } catch {
            return { id: Number(id), error: true };
          }
        })
      );
      setPageRows(rows);
    } catch (err) {
      notify.error(parseError(err) || 'Could not load users page.');
    } finally {
      setPageLoading(false);
    }
  }

  // Only fetch when the admin has explicitly opted in (usersLoaded === true)
  useEffect(() => {
    if (stats && usersLoaded) loadPage(page);
    // eslint-disable-next-line
  }, [stats, page, usersLoaded]);

  async function doLookup() {
    setLookupErr('');
    setResult(null);
    const raw = query.trim();
    if (!raw) return;
    setLookupBusy(true);
    try {
      let userId = 0;
      if (/^0x[a-fA-F0-9]{40}$/.test(raw)) {
        const id = await read.storage.addressToId(raw);
        userId = Number(id);
        if (!userId) {
          setLookupErr('No user registered for this wallet.');
          return;
        }
      } else if (/^\d+$/.test(raw)) {
        userId = Number(raw);
      } else {
        setLookupErr('Enter a valid user ID (number) or wallet (0x...).');
        return;
      }

      const [basic, income, isBl, nextClick, pkgCount] = await Promise.all([
        read.storage.getUserBasic(userId),
        read.storage.getIncomeBreakdown(userId),
        read.storage.blacklisted(userId).catch(() => false),
        read.storage.getNextMiningClickTime(userId).catch(() => ethers.BigNumber.from(0)),
        read.storage.getPackageCount(userId).catch(() => ethers.BigNumber.from(0)),
      ]);

      if (!basic || !basic.wallet || basic.wallet === ethers.constants.AddressZero) {
        setLookupErr('User not found.');
        return;
      }

      setResult({
        userId,
        wallet: basic.wallet,
        sponsorId: Number(basic.sponsorId),
        totalInvestment: basic.totalInvestment,
        pendingBalance: basic.pendingBalance,
        totalWithdrawn: basic.totalWithdrawn,
        directs: Number(basic.directs),
        qualifying: Number(basic.qualifying),
        isActive: basic.isActive,
        isBlacklisted: isBl,
        lifetimeMining: income.lifetimeMining,
        lifetimeLevelIncome: income.lifetimeLevelIncome,
        pkgCount: Number(pkgCount),
        nextClick: Number(nextClick),
      });
    } catch (err) {
      setLookupErr(parseError(err) || 'Lookup failed.');
    } finally {
      setLookupBusy(false);
    }
  }

  return (
    <div className="admin-panel">
      <h3>Platform Stats & User Lookup</h3>
      <p className="panel-hint">Live totals and per-user drill-down straight from the contract.</p>

      <div style={{ marginTop: 8, marginBottom: 12 }}>
        <button className="btn btn-secondary btn-sm" onClick={loadStats} disabled={statsLoading}>
          {statsLoading ? <><Spinner /> Loading…</> : '↻ Refresh Stats'}
        </button>
      </div>

      {/* SUMMARY TILES */}
      <div style={{
        display: 'grid', gap: 12, marginBottom: 20,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      }}>
        <StatTile
          label="Total Users"
          value={stats ? stats.totalUsers.toLocaleString('en-US') : '—'}
          hint="Registered wallets on the platform"
        />
        <StatTile
          label="Total Business"
          value={stats ? formatUSDT(stats.totalInvestment, 0) : '—'}
          unit="USDT"
          hint="Lifetime investments (real + virtual)"
        />
        <StatTile
          label="Withdrawn as USDT"
          value={stats ? formatUSDT(stats.totalWithdrawUsdt, 0) : '—'}
          unit="USDT"
          hint="Lifetime gross paid out in USDT"
        />
        <StatTile
          label="Withdrawn as XT"
          value={stats ? formatUSDT(stats.totalWithdrawXt, 0) : '—'}
          unit="USDT"
          hint="Lifetime gross paid out in XT (USD-equivalent)"
        />
        <StatTile
          label="Mining Income Paid"
          value={stats ? formatUSDT(stats.totalMining, 0) : '—'}
          unit="USDT"
          hint="Lifetime mining income credited"
        />
        <StatTile
          label="Level Income Paid"
          value={stats ? formatUSDT(stats.totalLevel, 0) : '—'}
          unit="USDT"
          hint="Lifetime level income credited"
        />
      </div>

      <div className="divider" />

      {/* USERS LIST — 10 per page, opt-in load */}
      <UsersList
        stats={stats}
        page={page}
        setPage={setPage}
        rows={pageRows}
        loading={pageLoading}
        loaded={usersLoaded}
        onLoad={() => setUsersLoaded(true)}
        onReload={() => { setUsersLoaded(true); loadPage(page); }}
        onQuickLookup={(id) => { setQuery(String(id)); setTimeout(() => doLookup(), 0); }}
      />

      <div className="divider" />

      {/* USER LOOKUP */}
      <h4 style={{ marginBottom: 8 }}>User Lookup</h4>
      <p className="panel-hint" style={{ marginTop: 0 }}>
        Enter a user ID (e.g. <code>5000</code>) or a wallet address (0x…).
      </p>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">User ID or Wallet</label>
            <input
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="1234  or  0xabc…"
              onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }}
            />
          </div>
        </div>
        <button
          className="btn btn-primary"
          disabled={lookupBusy || !query.trim()}
          onClick={doLookup}
        >
          {lookupBusy ? <Spinner /> : 'Look Up'}
        </button>
      </div>

      {lookupErr && (
        <div style={{
          padding: 12, marginTop: 10,
          background: 'rgba(255, 77, 94, 0.06)',
          border: '1px solid rgba(255, 77, 94, 0.35)',
          borderRadius: 8, color: 'var(--error)', fontSize: 13,
        }}>
          {lookupErr}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          }}>
            <KV label="User ID" value={String(result.userId)} mono />
            <KV label="Wallet" value={shortenAddress(result.wallet)} mono />
            <KV label="Sponsor ID" value={String(result.sponsorId)} mono />
            <KV label="Status" value={
              result.isBlacklisted ? '🚫 Blacklisted'
                : result.isActive ? '● Active'
                : '○ Inactive'
            } warn={result.isBlacklisted} />
            <KV label="Packages" value={String(result.pkgCount)} />
            <KV label="Directs (total)" value={String(result.directs)} />
            <KV label="Directs (qualifying)" value={String(result.qualifying)} />
            <KV label="Next Mining Click"
              value={result.nextClick ? formatDate(result.nextClick) : '—'} />
          </div>

          <div className="divider" />

          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}>
            <KV label="Total Investment"      value={`${formatUSDT(result.totalInvestment)} USDT`} accent />
            <KV label="Pending Balance"       value={`${formatUSDT(result.pendingBalance)} USDT`} accent />
            <KV label="Total Withdrawn"       value={`${formatUSDT(result.totalWithdrawn)} USDT`} />
            <KV label="Lifetime Mining"       value={`${formatUSDT(result.lifetimeMining)} USDT`} />
            <KV label="Lifetime Level Income" value={`${formatUSDT(result.lifetimeLevelIncome)} USDT`} />
          </div>
        </div>
      )}
    </div>
  );
}

function UsersList({ stats, page, setPage, rows, loading, loaded, onLoad, onReload, onQuickLookup }) {
  const total = stats ? stats.totalUsers : 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clamp = (n) => Math.min(pageCount, Math.max(1, n));

  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to   = Math.min(total, page * PAGE_SIZE);

  // Not loaded yet — show a big placeholder with a Load button to save RPC calls
  if (!loaded) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>All Users</h4>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {total > 0 ? `${total.toLocaleString('en-US')} total` : 'No users yet.'}
          </span>
        </div>
        <div style={{
          padding: 32, textAlign: 'center',
          background: 'var(--bg-tertiary)',
          border: '1px dashed var(--border)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>👥</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
            User list is not loaded to save RPC calls.<br />
            Click below to fetch the first {PAGE_SIZE} users.
          </div>
          <button
            className="btn btn-primary"
            onClick={onLoad}
            disabled={total === 0}
          >
            📥 Load Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0 }}>All Users</h4>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {total > 0 ? `Showing ${from}–${to} of ${total.toLocaleString('en-US')}` : 'No users yet.'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onReload}
            disabled={loading}
            title="Re-fetch this page"
          >↻</button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={loading || page <= 1}
            onClick={() => setPage(clamp(page - 1))}
          >‹ Prev</button>
          <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', minWidth: 60, textAlign: 'center' }}>
            Page {page} / {pageCount}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={loading || page >= pageCount}
            onClick={() => setPage(clamp(page + 1))}
          >Next ›</button>
        </div>
      </div>

      <div style={{ marginTop: 12, overflowX: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'separate', borderSpacing: 0,
          minWidth: 720, fontSize: 13,
        }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>User ID</th>
              <th style={thStyle}>Wallet</th>
              <th style={thStyle}>Sponsor</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Investment</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Pending</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Pkgs</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Directs</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Spinner /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                No users on this page.
              </td></tr>
            )}
            {rows.map((r, i) => {
              if (r.error) {
                return (
                  <tr key={r.id}>
                    <td style={tdStyle}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{r.id}</td>
                    <td colSpan={8} style={{ ...tdStyle, color: 'var(--error)' }}>Failed to load.</td>
                  </tr>
                );
              }
              const status = r.isBlacklisted ? { label: '🚫 BL', color: 'var(--error)' }
                : r.isActive ? { label: '● Active', color: 'var(--accent)' }
                : { label: '○ Idle', color: 'var(--text-muted)' };
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                  <td style={tdMuted}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>
                    {r.id}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{shortenAddress(r.wallet)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{r.sponsorId || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {formatUSDT(r.totalInvestment, 0)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {formatUSDT(r.pendingBalance, 2)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{r.pkgCount}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{r.directs}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: status.color, fontWeight: 700, fontSize: 12 }}>
                    {status.label}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => onQuickLookup(r.id)}>
                      Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && rows.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          <Spinner /> Refreshing…
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '10px 12px', fontWeight: 700,
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-tertiary)',
};
const tdStyle = {
  padding: '10px 12px', color: 'var(--text-primary)',
  verticalAlign: 'middle',
};
const tdMuted = { ...tdStyle, color: 'var(--text-muted)', fontSize: 12 };

function StatTile({ label, value, unit, hint }) {
  return (
    <div style={{
      padding: 16, background: 'var(--bg-tertiary)',
      border: '1px solid var(--border-soft)', borderRadius: 12,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
      }}>{label}</div>
      <div style={{
        marginTop: 6, fontSize: 22, fontWeight: 800,
        fontFamily: 'var(--font-mono)', color: 'var(--accent)',
      }}>
        {value}{unit && <span style={{ fontSize: 12, marginLeft: 6, color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      {hint && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>
      )}
    </div>
  );
}

function KV({ label, value, mono, accent, warn }) {
  return (
    <div style={{
      padding: 12, background: 'var(--bg-tertiary)',
      border: '1px solid var(--border-soft)', borderRadius: 10,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
      }}>{label}</div>
      <div style={{
        marginTop: 4,
        fontSize: 14, fontWeight: 700,
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        color: warn ? 'var(--error)' : accent ? 'var(--accent)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}
